import styled, { css } from 'styled-components';

import { getConsumptionValue } from 'shared/helpers/helpers';
import {
  STATUS,
  getEbpLocked,
  getMachineTemperatureDisplay,
  getProgramStatuses,
  getSetTemp,
  getSwitchStatus,
} from './zoneDetailUtils';

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" stroke="#51a2ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const getStatusDotColor = (status) => {
  if (status === STATUS.ACTIVE) return '#00bc7d';
  if (status === STATUS.FAULTS) return '#ef4444';
  if (status === STATUS.READY) return '#3b82f6';
  if (status === STATUS.OFF) return '#f97316';
  return '#475569';
};

const MonitoredSwitchCard = ({ swtName, machineId, machineData, isF, onSelect }) => {
  const status = getSwitchStatus(machineData);
  const temp = getMachineTemperatureDisplay(machineData, isF);
  const setTempValue = getSetTemp(machineData, isF);
  const dotColor = getStatusDotColor(status);
  const programStatuses = getProgramStatuses(machineData, swtName);

  const isOff = machineData?.isOff;
  const isFaults = machineData?.isFaults;
  const isWifi = machineData?.isWifi;
  const isGp = machineData?.isGp;
  const isEbp = machineData?.isEbp;
  const consumption = machineData?.consumption;
  const ebpLocked = getEbpLocked(machineData);

  return (
    <Card type="button" onClick={() => onSelect(machineId)} $isOff={isOff}>
      <GlowOverlay />

      <CardTop>
        <NameGroup>
          <StatusDot $color={dotColor} />
          <MachineName>{machineData?.machineName || machineId}</MachineName>
        </NameGroup>
        <StatusIcons>
          <StatusIcon
            src={isWifi ? '/images/wifi-active.svg' : '/images/wifi-none.svg'}
            alt="WiFi"
            $dimmed={!isWifi}
          />
          <StatusIcon
            src={isFaults ? '/images/fault-sm-active.svg' : '/images/fault-sm.svg'}
            alt="Faults"
            $dimmed={!isFaults}
          />
        </StatusIcons>
      </CardTop>

      <BadgeRow>
        {isGp != null && (
          <PowerBadge $active={isGp} $color="#00d492">GP</PowerBadge>
        )}
        {isEbp != null && (
          <PowerBadge $active={isEbp} $color="#f97316">EBP</PowerBadge>
        )}
        {ebpLocked && <PowerBadge $active $color="#ee4b56">EBP LOCK</PowerBadge>}
      </BadgeRow>

      <CardMiddle>
        <TempBlock>
          <TempLabel>Current Temp</TempLabel>
          <TempRow>
            <TempValue>{temp.value}</TempValue>
            <TempUnit>{`\u00B0${temp.unit}`}</TempUnit>
          </TempRow>
        </TempBlock>

        <TempBlock>
          <TempLabel>Set Temp</TempLabel>
          <TempRow>
            <SetTempValue>{setTempValue}</SetTempValue>
            {setTempValue !== '--' && <TempUnit>{`\u00B0${isF ? 'F' : 'C'}`}</TempUnit>}
          </TempRow>
        </TempBlock>
      </CardMiddle>

      {consumption != null && (
        <ConsumptionRow>
          <ConsumptionLabel>Energy</ConsumptionLabel>
          <ConsumptionValue>
            {getConsumptionValue(swtName, consumption, isF)}
            <ConsumptionUnit>
              {swtName === 'tgs' ? (isF ? 'FT³/H' : 'M³/H') : 'KW/H'}
            </ConsumptionUnit>
          </ConsumptionValue>
        </ConsumptionRow>
      )}

      <CardBottom>
        <ProgramDots>
          {programStatuses.map(({ key, meta, isActive, isReady }) => (
            <ProgramDotWrapper key={key} title={meta.label}>
              <ProgramDotIcon src={meta.icon} alt={meta.shortLabel} />
              <ProgramDotIndicator $active={isActive} $ready={isReady} />
            </ProgramDotWrapper>
          ))}
        </ProgramDots>

        <ArrowCircle>
          <ArrowIcon />
        </ArrowCircle>
      </CardBottom>

      {isOff && <OffOverlay>FROZEN</OffOverlay>}
    </Card>
  );
};

export default MonitoredSwitchCard;



const Card = styled.button`
  position: relative;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 28px;
  border-radius: 16px;
  border: 0.8px solid var(--switch-border);
  background: var(--switch-card-bg);
  color: inherit;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  box-shadow: var(--switch-shadow-soft);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  ${({ $isOff }) => $isOff && css`
    opacity: 0.6;
    border-color: rgba(249, 115, 22, 0.3);
  `}

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(59, 130, 246, 0.45);
    box-shadow: var(--switch-shadow-popover);
  }

  @media (max-width: 720px) {
    width: 100%;
    min-width: 0;
  }
`;

const GlowOverlay = styled.div`
  position: absolute;
  top: -40px;
  right: -10px;
  width: 128px;
  height: 128px;
  border-radius: 9999px;
  background: rgba(43, 127, 255, 0.05);
  filter: blur(64px);
  pointer-events: none;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const NameGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 8px ${({ $color }) => `${$color}80`};
  flex-shrink: 0;
`;

const MachineName = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.35px;
  color: var(--switch-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StatusIcon = styled.img`
  width: 14px;
  height: 14px;
  ${({ $dimmed }) => $dimmed && 'opacity: 0.4;'}
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const PowerBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: ${({ $active, $color }) => ($active ? $color : 'var(--switch-text-faint)')};
  background: ${({ $active, $color }) => ($active ? `${$color}18` : 'transparent')};
  border: 0.8px solid ${({ $active, $color }) => ($active ? `${$color}30` : 'var(--switch-border)')};
`;

const CardMiddle = styled.div`
  display: flex;
  gap: 20px;
`;

const CardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1;
`;

const TempBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TempLabel = styled.span`
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--switch-text-faint);
`;

const TempRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 3px;
`;

const TempValue = styled.span`
  font-family: 'Consolas', monospace;
  font-size: 30px;
  font-weight: 700;
  line-height: 36px;
  letter-spacing: -1.5px;
  color: var(--switch-text-primary);
`;

const TempUnit = styled.span`
  font-family: 'Consolas', monospace;
  font-size: 14px;
  font-weight: 400;
  color: var(--switch-text-muted);
`;

const SetTempValue = styled.span`
  font-family: 'Consolas', monospace;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -1px;
  color: var(--switch-text-muted);
`;

const ConsumptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 10px;
  background: var(--switch-panel-bg-subtle);
  border: 0.8px solid var(--switch-border-soft);
`;

const ConsumptionLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--switch-text-faint);
`;

const ConsumptionValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--switch-text-primary);
`;

const ConsumptionUnit = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: var(--switch-text-faint);
  margin-left: 4px;
`;

const ProgramDots = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
`;

const ProgramDotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const ProgramDotIcon = styled.img`
  width: 14px;
  height: 14px;
  opacity: 0.7;
`;

const ProgramDotIndicator = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $active, $ready }) =>
    $active ? '#00d492' : $ready ? '#3b82f6' : 'var(--switch-control-track)'};
  box-shadow: ${({ $active, $ready }) =>
    $active ? '0 0 6px #00d49280' : $ready ? '0 0 6px #3b82f680' : 'none'};
`;

const ArrowCircle = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(43, 127, 255, 0.1);

  svg {
    transform: rotate(-45deg);
    width: 16px;
    height: 16px;
  }
`;

const OffOverlay = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 2px 10px;
  border-radius: 4px;
  background: rgba(249, 115, 22, 0.15);
  border: 0.8px solid rgba(249, 115, 22, 0.3);
  color: #f97316;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
`;
